<template>
	<div class="step" :class="{'height-restricted' : restrictedHeight, 'is-loading' : isLoading }">
		<!-- Navigation -->
		<div class="step__nav type--sans" v-if="!hideNavigation">
			<BookingStepperNav v-on="$listeners" :prev="prevNavigation" :next="nextNavigation"></BookingStepperNav>
		</div>

		<div class="step__main">
			<div class="step__main-container">
				<!--  Main section header -->
				<div class="step__header" v-if="isHeaderVisible">
					<h2 class="step__title type--sans type--m type--b">{{ title }}</h2>
					<p v-if="!!lead" class="type--serif">{{ lead }}</p>
				</div>
				<!-- Default slot -->
				<slot></slot>
			</div>
		</div>

		<!-- Footer -->
		<div class="step__footer" v-if="!!$slots.footer">
			<slot name="footer"></slot>
		</div>
	</div>
</template>

<script>
import BookingStepperNav from "@/components/booking/form/BookingStepperNav"

export default {
	name: "StepTemplate",

	components: { BookingStepperNav },

	props: {
		restrictedHeight: {
			type: Boolean,
			default: false,
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		hideNavigation: {
			type: Boolean,
			default: false,
		},
		nextNavigation: {
			type: Object,
		},
		prevNavigation: {
			type: Object,
		},
		title: {
			type: String,
			default: "",
		},
		lead: {
			type: String,
			default: "",
		},
	},

	computed: {
		isHeaderVisible() {
			return !!this.title || !!this.lead
		},
	},

	mounted() {
		this.$on("prev", this.prev)
		this.$on("next", this.next)
	},

	methods: {
		prev() {
			console.log("prev")
		},
		next() {
			console.log("next")
		},
	},
}
</script>

<style lang="scss" scoped>
.step {
	display: flex;
	flex-direction: column;
	height: 100%;
	position: relative;

	// Force some steps to be at least viewport height on mobile
	&.height-restricted {
		height: auto;
		min-height: calc(100vh - #{$navbar-height});
		min-height: calc((var(--vh, 1vh) * 100) - #{$navbar-height});
		// Restrict height to be no taller than initially rendered form
		@media screen and (min-width: $width-tablet-medium) {
			height: var(--calculated-stepper-height);
			max-height: calc(100vh - (#{$navbar-height} + 2 * var(--stepper-padding)));
			min-height: initial;
		}
	}

	// Inactive loading propeller spinner
	&:after {
		background-image: asset("./icons_propeller--teal.svg");
		background-repeat: no-repeat;
		background-size: contain;
		content: "";
		height: 3rem;
		left: calc(50% - 1.5rem);
		opacity: 0;
		pointer-events: none;
		position: absolute;
		top: calc(50% - 1.5rem);
		transition: opacity 450ms;
		width: 3rem;
	}
	// Active loading propeller spinner
	&.is-loading {
		&:after {
			animation: spinner 1s linear infinite;
			opacity: 1;
		}
	}
}

.step__nav {
	background: #fff;
	border-bottom: 1px solid $colour-pencil--lightest;
	padding: 1rem;
	position: sticky;
	top: $navbar-height;
	z-index: 3;
	// Undo sticky header on wider screens
	@media screen and (min-width: $width-tablet-medium) {
		border-bottom: none;
		border-radius: 0.25rem 0.25rem 0 0;
		box-shadow: 0 1.125rem 0.5rem -1.25rem #000;
		position: relative;
		top: initial;
		z-index: 1;
	}
}

.step__main {
	flex: 1 0 auto;
	height: 100%;
	position: relative;
}

// Add scrollbars to stes with height restrictions
.height-restricted .step__main {
	@media screen and (min-width: $width-tablet-medium) {
		flex: initial;
		overflow-y: auto;
		overflow-y: overlay;
	}
	// Scroll shadow covers
	&:before,
	&:after {
		content: "";
		display: block;
		height: 1.25rem;
		left: 0;
		width: 100%;
		z-index: 2;
	}
	// Top scroll-shadow cover
	&:before {
		background: linear-gradient(0deg, rgba(255, 255, 255, 0), #fff 75%);
		border-radius: 0.25rem 0.25rem 0 0;
		position: absolute;
		top: 0;
		transform: translateY(-1px);
	}
	// Bottom scroll-shadow cover
	&:after {
		background: linear-gradient(180deg, rgba(255, 255, 255, 0), #fff 75%);
		border-radius: 0 0 0.25rem 0.25rem;
		top: 100%;
		transform: translateY(1px);
		position: sticky;
	}
}

.step__main-container {
	padding: 1rem 1.25rem 1.25rem;
	// Remove bottom padding, to allow for the .step__main:after position sticky shadow cover
	.height-restricted & {
		@media screen and (min-width: $width-tablet-medium) {
			padding: 1rem 1.25rem 0;
		}
	}
}

.step__header {
	margin-bottom: 1rem;
}

.step__title {
	line-height: 1.1;
	margin-bottom: 0.5rem;
}

.step__lead {
	margin-bottom: 0.5rem;
}

// Footer
.step__footer {
	background: #fff;
	border-top: 1px solid $colour-pencil--lightest;
	bottom: 0;
	padding: 1rem;
	position: sticky;
	z-index: 3;
	// Undo sticky footer on wider screens
	@media screen and (min-width: $width-tablet-medium) {
		border-radius: 0 0 0.25rem 0.25rem;
		border-top: none;
		box-shadow: 0 -1.125rem 0.5rem -1.25rem #000;
		position: relative;
		bottom: initial;
		z-index: 1;
	}
}
</style>